import { useState, useEffect } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { Trophy, TrendingDown, TrendingUp, Medal, AlertTriangle, Lock, Users, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { Spinner } from './ChallengeShared';
import { UserAvatar } from '../UserAvatar';

function PrixSection({ icon, title, entries, scoreKey, rawKey, sansCaution, ghostKey, classement }) {
  const medals = ['text-yellow-400', 'text-zinc-400', 'text-amber-700'];
  // Use classement (full sorted list for this specific prize) if available
  const fullRanking = classement || entries;
  return (
    <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4" data-testid={`prix-${scoreKey}`}>
      <div className="flex items-center gap-2 mb-3">{icon}<h3 className="text-sm font-bold text-white">{title}</h3></div>

      {/* Official ranking (with caution only) */}
      <div className="mb-1">
        <p className="text-[9px] text-[#2e7d32] uppercase tracking-wider font-bold mb-2 flex items-center gap-1">
          <Crown className="w-3 h-3" /> Classement officiel (avec caution)
        </p>
      </div>
      {fullRanking.length === 0 ? (
        <p className="text-xs text-zinc-500 text-center py-2">Aucun participant eligible</p>
      ) : (
        <div className="space-y-2.5">
          {fullRanking.slice(0, 10).map((e, i) => {
            const isPenalized = e.transparence?.penalized;
            const rawScore = e[rawKey];
            const finalScore = e[scoreKey];
            return (
              <div key={e.id} className="flex items-center gap-3">
                <span className={`w-6 text-center font-mono text-sm font-bold ${medals[i] || 'text-zinc-500'}`}>
                  {i < 3 ? <Medal className={`w-5 h-5 ${medals[i]}`} /> : `${i + 1}`}
                </span>
                <UserAvatar name={e.nom} photoPath={e.profile_photo} size="sm" isChallenger />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-white truncate block">{e.nom}</span>
                  {isPenalized && (
                    <span className="text-[9px] text-red-400 flex items-center gap-0.5">
                      <AlertTriangle className="w-2.5 h-2.5" /> Transparence {e.transparence.pct}%
                    </span>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`font-mono text-sm font-bold ${isPenalized ? 'text-red-400' : 'text-[#FF6B35]'}`}>
                    {finalScore?.toFixed(1)}
                  </span>
                  {isPenalized && rawScore !== undefined && (
                    <span className="text-[9px] text-zinc-500 block">avant: {rawScore?.toFixed(1)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sans caution — ghost positions */}
      {sansCaution && sansCaution.filter(p => p.has_data).length > 0 && (
        <div className="mt-4 pt-3 border-t border-[#2A2A2E]/50">
          <p className="text-[9px] text-zinc-500 uppercase tracking-wider font-bold mb-2 flex items-center gap-1">
            <Lock className="w-3 h-3" /> Sans caution — classement fictif
          </p>
          <div className="space-y-2">
            {sansCaution.filter(p => p.has_data).slice(0, 15).map(sc => {
              const ghostPos = sc.ghost_position?.[ghostKey];
              const score = sc[scoreKey];
              return (
                <div key={sc.id} className="flex items-center gap-2.5 bg-[#0F0F0F] rounded-sm px-3 py-2 border border-[#2A2A2E]/30">
                  <UserAvatar name={sc.nom} photoPath={sc.profile_photo} size="xs" />
                  <span className="text-xs text-zinc-400 flex-1 truncate">{sc.nom}</span>
                  {ghostPos && (
                    <span className="text-[10px] bg-[#FF6B35]/10 text-[#FF6B35] px-2 py-0.5 rounded-full font-mono font-bold" data-testid={`ghost-${sc.id}`}>
                      Tu aurais ete #{ghostPos}
                    </span>
                  )}
                  <span className="font-mono text-[10px] text-zinc-500">{score?.toFixed(1)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sans donnees */}
      {sansCaution && sansCaution.filter(p => !p.has_data).length > 0 && (
        <div className="mt-2 pt-2 border-t border-[#2A2A2E]/30">
          <p className="text-[9px] text-zinc-700 italic">
            +{sansCaution.filter(p => !p.has_data).length} membres sans mesures
          </p>
        </div>
      )}
    </div>
  );
}

export function PodiumTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/challenge/podium').then(r => setData(r.data)).catch(() => toast.error('Erreur')).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data) return null;

  const sc = data.sans_caution || [];
  const stats = data.stats || {};
  const officiel = data.classement_officiel || [];

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Stats */}
      <div className="flex items-center gap-4 px-3 py-2 bg-[#0F0F0F] rounded-lg border border-[#2A2A2E]">
        <Users className="w-4 h-4 text-[#FF6B35]" />
        <span className="text-xs text-zinc-400">
          <span className="text-[#2e7d32] font-bold">{stats.with_caution} avec caution</span> ·{' '}
          <span className="text-zinc-500">{stats.sans_caution} sans</span> ·{' '}
          <span className="text-zinc-600">{stats.total} total</span>
        </span>
      </div>

      {/* Info box */}
      <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 rounded-sm px-3 py-2">
        <p className="text-[10px] text-[#FF6B35]">
          Seuls les membres ayant verse leur caution sont eligibles au classement officiel. Les autres voient leur position fictive.
        </p>
      </div>

      <PrixSection icon={<Trophy className="w-5 h-5 text-[#FF6B35]" />} title="Recomposition (50/50)" entries={data.recomposition} scoreKey="score_prix1" rawKey="score_prix1_raw" sansCaution={data.sans_caution_recomposition || sc} ghostKey="recomposition" classement={data.classement_recomposition} />
      <PrixSection icon={<TrendingDown className="w-5 h-5 text-[#2e7d32]" />} title="Perte de gras" entries={data.perte_gras} scoreKey="score_mg" rawKey="score_mg_raw" sansCaution={data.sans_caution_perte_gras || sc} ghostKey="perte_gras" classement={data.classement_perte_gras} />
      <PrixSection icon={<TrendingUp className="w-5 h-5 text-[#3b82f6]" />} title="Prise de muscle" entries={data.prise_muscle} scoreKey="score_mm" rawKey="score_mm_raw" sansCaution={data.sans_caution_prise_muscle || sc} ghostKey="prise_muscle" classement={data.classement_prise_muscle} />

      {/* Full official ranking */}
      {officiel.length > 0 && (
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4" data-testid="classement-officiel-complet">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <Crown className="w-4 h-4 text-yellow-400" /> Classement officiel complet
          </h3>
          <div className="space-y-2">
            {officiel.map((e, i) => (
              <div key={e.id} className="flex items-center gap-3 py-1">
                <span className="w-6 text-center font-mono text-xs font-bold text-zinc-500">#{i + 1}</span>
                <UserAvatar name={e.nom} photoPath={e.profile_photo} size="xs" isChallenger />
                <span className="text-sm text-white flex-1 truncate">{e.nom}</span>
                <span className="font-mono text-xs text-[#FF6B35] font-bold">{e.score_prix1?.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
